import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { Star, Shield, Award, BadgeCheck } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { ReportMenu } from "@/components/ReportMenu";
import { VerifiedReferenceBadge } from "@/components/VerifiedReferenceBadge";
import { TrustedBadge } from "@/components/TrustedBadge";
import { getPublicProfileByUsername } from "@/lib/public-profile.functions";
import type { PublicProfileData } from "@/lib/public-profile.functions";

const profileQuery = (username: string) => ({
  queryKey: ["public-profile", username] as const,
  queryFn: () =>
    getPublicProfileByUsername({ data: { username } }) as Promise<PublicProfileData>,
});

export const Route = createFileRoute("/profile/$username")({
  head: ({ params }) => ({ meta: [{ title: `@${params.username} — Want To Buy` }] }),
  loader: ({ params, context }) =>
    context.queryClient.ensureQueryData(profileQuery(params.username)),
  component: Profile,
});

function Profile() {
  const { username } = Route.useParams();
  const { user } = useAuth();
  const { data } = useSuspenseQuery(profileQuery(username));
  const p = data.profile;
  const verifiedTotal = data.verifiedAsBuyer + data.verifiedAsSeller;
  const joined = p?.created_at
    ? new Date(p.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })
    : "—";

  return (
    <AppShell>
      <div className="panel group relative overflow-hidden p-6 sm:p-8">
        <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-primary/15 blur-3xl" />
        <div className="relative flex flex-wrap items-start gap-6">
          <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full bg-primary/20 font-display text-3xl font-bold text-primary">
            {p?.avatar_url ? (
              <img src={p.avatar_url} alt="" className="h-full w-full object-cover" />
            ) : (
              username[0]?.toUpperCase() ?? "?"
            )}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-3xl font-bold">@{username}</h1>
              {p?.trusted_seller && <TrustedBadge role="seller" link />}
              {verifiedTotal > 0 && (
                <VerifiedReferenceBadge count={verifiedTotal} size="md" link />
              )}
            </div>
            {p?.display_name && (
              <p className="text-sm text-foreground/80">{p.display_name}</p>
            )}
            <p className="text-sm text-muted-foreground">
              {p?.bio || "Public profile"}
            </p>
          </div>
          {user && (
            <div className="absolute right-4 top-4">
              <ReportMenu
                reportedUsername={username}
                context="profile"
                reportLabel="Report this user"
                size="md"
              />
            </div>
          )}
        </div>

        <div className="relative mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <RatingBlock
            label="Buyer rating"
            sub="From sellers who completed transactions with you"
            avg={p?.buyer_rating_avg ?? 0}
            count={p?.buyer_rating_count ?? 0}
            verified={data.verifiedAsBuyer}
          />
          <RatingBlock
            label="Seller rating"
            sub="From buyers who completed transactions with you"
            avg={p?.seller_rating_avg ?? 0}
            count={p?.seller_rating_count ?? 0}
            verified={data.verifiedAsSeller}
          />
        </div>

        <div className="relative mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metric
            label="Verified references"
            value={String(verifiedTotal)}
            icon={<BadgeCheck className="h-4 w-4 text-primary" />}
          />
          <Metric
            label="Completed sales"
            value={String(p?.seller_completed_count ?? 0)}
            icon={<Award className="h-4 w-4 text-muted-foreground" />}
          />
          <Metric label="Joined" value={joined} />
        </div>
      </div>

      <section className="mt-4 rounded-md border border-border bg-panel/60 p-4 text-xs text-muted-foreground">
        <div className="flex items-start gap-2">
          <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <div className="font-semibold text-foreground">Mutual reference system</div>
            <p className="mt-1">
              Ratings on Want To Buy are only unlocked once both parties independently confirm the transaction is complete.
              This prevents fake reviews and one-sided reputation attacks. <Link to="/rating-policy" className="text-primary hover:underline">How references work →</Link>
            </p>
          </div>
        </div>
      </section>

      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold">
            <Shield className="h-4 w-4 text-primary" /> Trust
          </h2>
          <div className="panel space-y-2 p-4 text-sm">
            <Row label="Buyer strikes" value={`${p?.buyer_strike_count ?? 0}/3`} />
            <Row
              label="Verified as buyer"
              value={
                <span className="inline-flex items-center gap-1">
                  <BadgeCheck className="h-3.5 w-3.5 text-primary" />
                  {data.verifiedAsBuyer}
                </span>
              }
            />
            <Row
              label="Verified as seller"
              value={
                <span className="inline-flex items-center gap-1">
                  <BadgeCheck className="h-3.5 w-3.5 text-primary" />
                  {data.verifiedAsSeller}
                </span>
              }
            />
            {p?.tcg_specialisations && p.tcg_specialisations.length > 0 && (
              <div className="pt-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Specialises in</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.tcg_specialisations.map((t) => (
                    <span key={t} className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
        <section>
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold">
            <BadgeCheck className="h-4 w-4 text-primary" /> Verified references
          </h2>
          {data.recent.length === 0 ? (
            <div className="panel p-6 text-center text-sm text-muted-foreground">
              No verified references yet.
            </div>
          ) : (
            <div className="panel divide-y divide-border">
              {data.recent.map((r) => (
                <div key={r.id} className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1 text-sm font-semibold">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`h-3.5 w-3.5 ${i < r.score ? "fill-warning text-warning" : "text-muted-foreground/40"}`}
                        />
                      ))}
                      <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                        <BadgeCheck className="h-3 w-3" /> Verified
                      </span>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {r.comment && <p className="mt-1.5 text-sm text-foreground/90">{r.comment}</p>}
                  {r.rater_username && (
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      — <Link to="/profile/$username" params={{ username: r.rater_username }} className="text-primary hover:underline">@{r.rater_username}</Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function Metric({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-background/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center gap-1.5 font-display text-2xl font-bold">
        {icon}
        {value}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function RatingBlock({
  label,
  sub,
  avg,
  count,
  verified,
}: {
  label: string;
  sub: string;
  avg: number;
  count: number;
  verified: number;
}) {
  if (count === 0) {
    return (
      <div className="rounded-md border border-border bg-background/40 p-4">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1 flex items-baseline gap-1.5">
          <Star className="h-4 w-4 text-muted-foreground" />
          <span className="font-display text-base font-bold text-muted-foreground">No references yet</span>
        </div>
        <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border bg-background/40 p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        {verified > 0 && <VerifiedReferenceBadge count={verified} size="xs" />}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <Star className="h-4 w-4 fill-warning text-warning" />
        <span className="font-display text-2xl font-bold">{Number(avg).toFixed(2)}</span>
        <span className="text-xs text-muted-foreground">({count})</span>
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}
